import { Component, computed, inject, input, type Signal } from "@angular/core";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { ViewReadinessService } from "@bitwarden/web-vault/app/services/view-readiness.service";

type DataSet = { text: string; amount: number; percent: string }[];

@Component({
  selector: "app-group-by-chart-control",
  templateUrl: "./group-by-chart-control.html",
})
export class GroupByChartControlComponent {
  currentView = input.required<View>();
  private calculationTodayStore = inject(CalculationTodayStoreService);
  protected userStoreService = inject(UserStoreService);
  private viewReadiness = inject(ViewReadinessService);

  protected isViewReady = computed(() =>
    this.currentView() === View.FUTURE
      ? this.viewReadiness.scenarioViewReady()
      : this.viewReadiness.todayViewReady(),
  );

  // Skeleton shown until the current view's calc is actually ready. Keyed off readiness
  // (not the transient isBackgroundLoading flag) so a slow/stuck calc keeps the skeleton
  // instead of going blank when the flag clears — this is !isViewReady(), so the
  // @if / @else if template pair is exhaustive (PR #3708 item 16).
  protected showBackgroundSkeleton = computed(() => {
    if (this.currentView() === View.FUTURE) {
      return !this.viewReadiness.scenarioViewReady();
    }
    return !this.viewReadiness.todayViewReady();
  });
  protected readonly ViewEnum = View;

  computedDataHash: string = "";
  private cachedComputedData: { dataSet: DataSet } = { dataSet: [] };

  // TODO: remove dataSet from computedData, fetch data from the store in the chart
  computedData: Signal<{ dataSet: DataSet }> = computed(() => {
    const accounts = this.calculationTodayStore.filteredAccounts.collection();
    const balances = this.calculationTodayStore.balanceByAccount.balances();

    if (!accounts || !balances) {
      this.computedDataHash = "";
      this.cachedComputedData = { dataSet: [] };
      return this.cachedComputedData;
    }

    const dataSet: DataSet = [];
    for (const account of accounts) {
      const amount = balances.get(account.id);
      if (amount !== 0) {
        dataSet.push({ text: account.displayName, amount, percent: "$" });
      }
    }

    const nextHash = dataSet
      .map((d) => `${d.text}:${d.amount}`)
      .sort()
      .join("|");
    if (nextHash === this.computedDataHash) {
      return this.cachedComputedData;
    }
    this.computedDataHash = nextHash;
    this.cachedComputedData = { dataSet };
    return this.cachedComputedData;
  });
}
